import { createRedirectUri } from './redirect-uri';

/**
 * Auth0 config-selection logic.
 *
 * The SPA SDK init itself is not meaningfully unit-testable, but *which*
 * domain/audience/clientId/redirect_uri it is pointed at — per environment —
 * is. That selection lives here as pure functions so it can be tested in
 * isolation, while `auth0.js` stays a thin `createAuth0(...)` wrapper.
 *
 * The real `@auth0/auth0-vue` SDK is initialised in every mode, including
 * `local`, so no code path is stubbed out:
 *   - local  → localauth0 (`VUE_APP_AUTH0_DOMAIN=localhost:37301`, local audience)
 *   - others → the real Auth0 tenant
 *
 * NOTE: in `local` mode the SDK is initialised but **not used to log in**.
 * localauth0's id_token cannot pass auth0-spa-js's OIDC validation (wrong
 * `aud`, missing `nonce`), so the persona picker mints a token directly
 * instead. The token is still a real localauth0-signed JWT that the API
 * validates normally. See `docs/adr/0003-local-login-mints-tokens-directly.md`
 * and `src/utils/local-auth.js`.
 */

export const LOCAL_NODE_ENV = 'local';

/**
 * True when the app is running against local dependencies (localauth0).
 * @param {Record<string, string>} [env=process.env]
 * @returns {boolean}
 */
export function isLocalAuthMode(env = process.env) {
  return env.VUE_APP_NODE_ENV === LOCAL_NODE_ENV;
}

/**
 * Build the options passed to `createAuth0` from the environment.
 * @param {Record<string, string>} [env=process.env]
 * @returns {import('@auth0/auth0-vue').Auth0VueClientOptions}
 */
export function buildAuth0Options(env = process.env) {
  const redirect_uri = createRedirectUri(env.VUE_APP_PACKAGE_ID, env.VUE_APP_AUTH0_DOMAIN);

  return {
    domain: env.VUE_APP_AUTH0_DOMAIN,
    clientId: env.VUE_APP_AUTH0_CLIENT_ID,
    authorizationParams: {
      redirect_uri,
      audience: env.VUE_APP_AUTH0_AUDIENCE,
    },
    cacheLocation: 'localstorage',
    useRefreshTokens: true,
  };
}
